/**
 * The Windows policy settings as the editor and the device tab present them. One table, so the editor,
 * the summary line and the tests cannot disagree about which settings exist. A test asserts every
 * member of the generated `WindowsPolicySettings` has a row.
 */
import { PolicyCheckState, PolicyEnforcement, type WindowsPolicySettings } from './contracts.gen.js';

export type SettingKey = keyof WindowsPolicySettings;

export interface SettingSpec {
  key: SettingKey;
  group: 'Security' | 'Windows Update' | 'Restrictions';
  /** The tick box's caption. A number setting says "at most" / "at least" here. */
  label: string;
  /** Present for a number setting: the box's unit and its allowed range. */
  number?: { unit: string; min: number; max: number; suggested: number };
  /** Short word for the summary line. */
  short: string;
}

export const SETTING_SPECS: readonly SettingSpec[] = [
  { key: 'requireBitLocker', group: 'Security', label: 'BitLocker is on for the system drive', short: 'BitLocker' },
  { key: 'requireDefenderRealtime', group: 'Security', label: 'Defender real-time protection is on', short: 'Defender' },
  { key: 'requireFirewall', group: 'Security', label: 'The firewall is on for every network profile', short: 'firewall' },
  { key: 'maxScreenLockMinutes', group: 'Security', label: 'The PC locks after at most', number: { unit: 'minutes idle', min: 1, max: 1440, suggested: 15 }, short: 'screen lock' },
  { key: 'minPasswordLength', group: 'Security', label: 'Passwords are at least', number: { unit: 'characters', min: 1, max: 14, suggested: 8 }, short: 'password length' },
  { key: 'maxPasswordAgeDays', group: 'Security', label: 'Passwords expire within', number: { unit: 'days', min: 1, max: 999, suggested: 90 }, short: 'password age' },
  { key: 'minPasswordHistory', group: 'Security', label: 'Windows remembers at least', number: { unit: 'previous passwords', min: 1, max: 24, suggested: 5 }, short: 'password history' },
  { key: 'maxLockoutThreshold', group: 'Security', label: 'Accounts lock after at most', number: { unit: 'failed sign-ins', min: 1, max: 999, suggested: 10 }, short: 'lockout' },
  { key: 'minQualityDeferralDays', group: 'Windows Update', label: 'Quality updates are deferred at least', number: { unit: 'days', min: 0, max: 30, suggested: 7 }, short: 'quality deferral' },
  { key: 'minFeatureDeferralDays', group: 'Windows Update', label: 'Feature updates are deferred at least', number: { unit: 'days', min: 0, max: 365, suggested: 60 }, short: 'feature deferral' },
  { key: 'requireNoAutoRestartWithUsers', group: 'Windows Update', label: 'Windows does not restart on its own while someone is signed in', short: 'no auto-restart' },
  { key: 'requireUsbStorageBlocked', group: 'Restrictions', label: 'USB storage is blocked', short: 'USB storage' },
  { key: 'requireMicrosoftAccountsBlocked', group: 'Restrictions', label: 'Microsoft accounts are blocked', short: 'Microsoft accounts' },
  { key: 'requireCameraDisabled', group: 'Restrictions', label: 'The camera is disabled', short: 'camera' },
  { key: 'requireStoreDisabled', group: 'Restrictions', label: 'The Microsoft Store is disabled (Enterprise and Education only)', short: 'Store' }
];

export const SETTING_GROUPS = ['Security', 'Windows Update', 'Restrictions'] as const;

export function emptySettings(): WindowsPolicySettings {
  const s = {} as Record<SettingKey, boolean | number | null>;
  for (const spec of SETTING_SPECS) s[spec.key] = null;
  return s as unknown as WindowsPolicySettings;
}

export function isChecked(settings: WindowsPolicySettings, key: SettingKey): boolean {
  const v = settings[key];
  return v !== null && v !== undefined && v !== false;
}

/** The editor's state for one row, turned into a setting value. A number that is not a whole number in range is a sentence. */
export function settingValue(spec: SettingSpec, checked: boolean, text: string): boolean | number | null | string {
  if (!checked) return null;
  if (!spec.number) return true;
  const t = text.trim();
  if (!/^\d+$/.test(t)) return `"${spec.label} ... ${spec.number.unit}" needs a whole number.`;
  const n = Number(t);
  if (n < spec.number.min || n > spec.number.max)
    return `"${spec.label} ... ${spec.number.unit}" is ${spec.number.min} to ${spec.number.max}.`;
  return n;
}

/** "BitLocker, firewall, screen lock 15 min" - what a policy checks, for its row. */
export function summarizeSettings(settings: WindowsPolicySettings): string {
  const parts: string[] = [];
  for (const spec of SETTING_SPECS) {
    const v = settings[spec.key];
    if (v === null || v === undefined || v === false) continue;
    parts.push(typeof v === 'number' && spec.number ? `${spec.short} ${v}` : spec.short);
  }
  return parts.length === 0 ? 'Checks nothing yet' : parts.join(', ');
}

export const CHECK_STATE_LABELS: Record<PolicyCheckState, string> = {
  [PolicyCheckState.Compliant]: 'Matches',
  [PolicyCheckState.NotCompliant]: 'Does not match',
  [PolicyCheckState.NotSupported]: 'Not available',
  [PolicyCheckState.Unknown]: 'Could not read'
};

/** The chip for what the agent did; None draws no chip. */
export const ENFORCEMENT_LABELS: Record<PolicyEnforcement, string> = {
  [PolicyEnforcement.None]: '',
  [PolicyEnforcement.Applied]: 'Set by the agent',
  [PolicyEnforcement.Failed]: 'Could not set',
  [PolicyEnforcement.InProgress]: 'In progress',
  [PolicyEnforcement.Reverted]: 'Set back, set again'
};

export function enforcementTone(kind: PolicyEnforcement): 'ok' | 'danger' | 'info' | 'warn' | 'neutral' {
  switch (kind) {
    case PolicyEnforcement.Applied:
      return 'ok';
    case PolicyEnforcement.Failed:
      return 'danger';
    case PolicyEnforcement.InProgress:
      return 'info';
    case PolicyEnforcement.Reverted:
      return 'warn';
    default:
      return 'neutral';
  }
}

export function checkStateTone(state: PolicyCheckState): 'ok' | 'danger' | 'neutral' | 'warn' {
  switch (state) {
    case PolicyCheckState.Compliant:
      return 'ok';
    case PolicyCheckState.NotCompliant:
      return 'danger';
    case PolicyCheckState.NotSupported:
      return 'neutral';
    default:
      return 'warn';
  }
}
